import { GridItem } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

import Image from '../Image';
import Motion from '../Motion';
import type { FooterCtaProps } from '.';

export type FooterCtaImageProps = Pick<FooterCtaProps, 'image' | 'variant'> & {
  readonly className?: string | undefined;
};

export const getImageSizes = (
  variant: FooterCtaImageProps['variant'],
): string => {
  if (variant.lg === 'two_column') {
    return '(max-width: 1023px) 90vw, (max-width: 1567px) 50vw, 785px';
  } else {
    return '(max-width: 1567px) 90vw, 1440px';
  }
};

const FooterCtaImage = ({ className, image, variant }: FooterCtaImageProps) => {
  const shouldReduceMotion = useReducedMotion();
  const target = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target,
    offset: ['start end', 'end start'],
  });
  const scrollY = useTransform(scrollYProgress, [0, 1], ['-20%', '5%']);

  return (
    <GridItem
      colEnd={{ sm: 'main', md: variant.lg === 'two_column' ? 14 : 'main' }}
      colStart={{ sm: 'main' }}
      rowStart={{ sm: 1 }}
    >
      <div
        className={cx(
          'relative overflow-hidden rounded-lg',
          {
            'h-96': variant.sm === 'two_column',
            'h-[545px]': variant.sm === 'background',
            'md:aspect-4/3 md:h-auto': variant.lg === 'two_column',
            'md:h-[500px] lg:h-[700px]': variant.lg === 'background',
          },
          className,
        )}
        data-cy="footer-cta-image-wrapper"
      >
        <Motion
          data-cy="footer-cta-image"
          initial={{ scale: 1.1 }}
          ref={target}
          style={{
            y: shouldReduceMotion ? 0 : scrollY,
            height: shouldReduceMotion ? '100%' : '115%',
          }}
          transition={{ duration: 0.75, type: 'tween' }}
          viewport={{ amount: 0.667, once: true }}
          whileInView={{ scale: 1 }}
        >
          <Image
            alt=""
            {...image}
            className="object-cover"
            fill
            sizes={getImageSizes(variant)}
          />
        </Motion>
      </div>
    </GridItem>
  );
};

export default FooterCtaImage;
